// @flow
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { Fragment } from 'react';
import { FormControl, FormHelperText } from "@mui/material";
import TextField from "@mui/material/TextField";
import { normalizeGreek } from "../../../../helpers/normalizeGreek";
import { ErrorMessage } from "formik";
import { EmailValid } from "../../../../hooks/useValidateEmail";
import ChangeVisitorCharacterization from "../../changeApprovalStatus/changeVisitorCharacterization";
import type { VisitorCategory } from "../../../../types/categories/type";
import { useTranslation } from "react-i18next";
import { EXHIBITOR_PROFILE } from "../../../../locales/components/namespaces";
import SelectCountry from "../../../form/selectCountries/selectCountries";
import type { Exhibition } from "../../../../types/exhibition/types";
import type { Countries } from "../../../../types/countries/type";
import { Error, VerifiedOutlined } from "@mui/icons-material";

type Props = {
	formik: Object,
	limitedView?: boolean,
	exhibitionId: string,
	exhibition: Object<Exhibition>,
	isShortOrganizerAddVisitor?: boolean,
	countries?: Array<Countries>
};

const FormFields = (props: Props) => {
	const {
		formik,
		limitedView = false,
		exhibitionId,
		exhibition,
		isShortOrganizerAddVisitor,
		countries,
		emailIsValid,
		validateEmail
	} = props
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const variant = 'filled'
	return (
		<Fragment>
			<FormControl
				sx={{ mb: 3 }}
				fullWidth
				error={
					formik.touched["name"] &&
					Boolean(formik.errors["name"])
				}
			>
				<TextField
					variant={variant}
					id="outlined-name"
					label={t("Name")}
					value={formik.values.name}
					name={`name`}
					size={"small"}
					onChange={(e) => {
						const oldValue = e.target.value
						e.target.value = normalizeGreek(oldValue.toUpperCase())
						formik.handleChange(e)
					}}
					onBlur={formik.handleBlur}
					fullWidth
					required
					autoComplete={"off"}
					slotProps={{
						htmlInput: {
							maxLength: 100,
							inputMode: 'text'
						},
					}}
				/>
				<FormHelperText>
					<ErrorMessage name={"name"}/>
				</FormHelperText>
			</FormControl>
			{isShortOrganizerAddVisitor && (
				<FormControl
					sx={{ mb: 3 }}
					fullWidth
					error={
						formik.touched["companyName"] &&
						Boolean(formik.errors["companyName"])
					}
				>
					<TextField
						variant={variant}
						id="outlined-name"
						label={t("Company Name")}
						value={formik.values.companyName}
						name={`companyName`}
						size={"small"}
						onChange={(e) => {
							const oldValue = e.target.value
							e.target.value = normalizeGreek(oldValue.toUpperCase())
							formik.handleChange(e)
						}}
						onBlur={formik.handleBlur}
						fullWidth
						autoComplete={"off"}
						slotProps={{
							htmlInput: {
								maxLength: 100,
								inputMode: 'text'
							},
						}}
					/>
					<FormHelperText>
						<ErrorMessage name={"companyName"}/>
					</FormHelperText>
				</FormControl>
			)}
			<FormControl
				sx={{ mb: 3 }}
				fullWidth
				error={
					formik.touched["email"] &&
					Boolean(formik.errors["email"])
				}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
					<TextField
						variant={variant}
						id="outlined-email"
						label={t("Email")}
						value={formik.values.email}
						name={`email`}
						size={"small"}
						onChange={formik.handleChange}
						onBlur={(evt) => {
							formik.handleBlur(evt)
							validateEmail(evt.target.value)
						}}
						fullWidth
						required={!isShortOrganizerAddVisitor}
						autoComplete={"off"}
						slotProps={{
							htmlInput: {
								maxLength: 100,
								inputMode: 'text'
							},
						}}
						sx={{
							'& .MuiFilledInput-root': {
								border: emailIsValid === EmailValid.Valid ? '1px ridge #4caf50' : emailIsValid === EmailValid.Error ? '1px ridge #ed6c02' : 'auto',
							}
						}}
					/>
					<Box>
						{emailIsValid === EmailValid.Valid ? (
							<VerifiedOutlined color={'success'} sx={{ marginLeft: 0.5 }}/>
						) : (emailIsValid === EmailValid.Error) ? (
							<Error color={'warning'} sx={{ marginRight: 0.5 }}/>
						) : null}
					</Box>
				</Box>
				<FormHelperText>
					<ErrorMessage name={"email"}/>
				</FormHelperText>
				{emailIsValid === EmailValid.Error && (
					<Typography variant="body2" sx={{ marginLeft: 1, color: '#ed6c02' }}>
						{t('Email may be incorrect.')}
					</Typography>
				)}
			</FormControl>
			{!isShortOrganizerAddVisitor && (
				<FormControl
					sx={{ mb: 3 }}
					fullWidth
					error={
						formik.touched["mobilePhone"] &&
						Boolean(formik.errors["mobilePhone"])
					}
				>
					<TextField
						variant={variant}
						id="outlined-mobilePhone"
						label={t("Mobile Phone")}
						type={'number'}
						value={formik.values.mobilePhone}
						name={`mobilePhone`}
						size={"small"}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						fullWidth
						autoComplete={"off"}
						// InputProps={{
						// 	startAdornment: (
						// 		<InputAdornment position="start">
						// 			<img
						// 				src="https://upload.wikimedia.org/wikipedia/commons/5/5c/Flag_of_Greece.svg"
						// 				alt="Greece"
						// 				style={{
						// 					width: "24px",
						// 					height: "16px",
						// 					objectFit: "cover",
						// 					borderRadius: "2px",
						// 				}}
						// 			/>
						// 		</InputAdornment>
						// 	),
						// }}
					/>
					<FormHelperText>
						<ErrorMessage name={"mobilePhone"}/>
					</FormHelperText>
				</FormControl>
			)}
			{isShortOrganizerAddVisitor && (
				<FormControl
					sx={{ mb: 3 }}
					fullWidth
					error={
						formik.touched["countries"] &&
						Boolean(formik.errors["countries"])
					}
				>
					<SelectCountry
						formik={formik}
						label={t("Country")}
						onChange={(event, newValue) => {
							formik.setFieldValue('countries', newValue)
						}}
						value={formik.values.countries}
						onBlur={formik.handleBlur}
						countries={countries}
						required={true}
						name={`countries`}
						autoComplete={"off"}
						variant={'filled'}
					/>
					<FormHelperText>
						<ErrorMessage name={"countries"}/>
					</FormHelperText>
				</FormControl>
			)}
			{!limitedView && (
				<>
					<FormControl
						sx={{ mb: 3 }}
						fullWidth
						error={
							formik.touched["vatNumber"] &&
							Boolean(formik.errors["vatNumber"])
						}
					>
						<TextField
							variant={variant}
							label={t("VAT Number")}
							value={formik.values.vatNumber}
							name={`vatNumber`}
							size={"small"}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							autoComplete={"off"}
							slotProps={{
								htmlInput: {
									maxLength: 20,
									inputMode: 'text'
								},
							}}
						/>
						<FormHelperText>
							<ErrorMessage name={"vatNumber"}/>
						</FormHelperText>
					</FormControl>
					<Box sx={{ mb: 3 }}>
						<ChangeVisitorCharacterization
							shrink={true}
							exhibitionId={exhibitionId}
							onChange={(category: VisitorCategory) => {
								formik.setFieldValue('characterization', category ? category.id : '')
							}}
							selected={formik.values.characterization}
							formik={formik}
							required={!limitedView && exhibition?.visitorCategoryRequiredInImport}
						/>
					</Box>
				</>
			)}
			{!isShortOrganizerAddVisitor && (
				<FormControl
					fullWidth
					error={
						formik.touched["city"] &&
						Boolean(formik.errors["city"])
					}
				>
					<TextField
						variant={variant}
						id="outlined-name"
						label={t("City")}
						value={formik.values.city}
						name={`city`}
						size={"small"}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						fullWidth
						autoComplete={"off"}
						slotProps={{
							htmlInput: {
								maxLength: 50,
								inputMode: 'text'
							},
						}}
					/>
					<FormHelperText>
						<ErrorMessage name={"city"}/>
					</FormHelperText>
				</FormControl>
			)}
		</Fragment>
	);
};

export default FormFields